import { useMemo } from 'react'
import { useQuery, useQueries, useInfiniteQuery } from '@tanstack/react-query'
import type { AccountsPage } from '@/services/api/accountApi'
import { usePools } from './use-pools'
import { apiService } from '@/services/apiService'
import type { Account } from '@/services/api/accountApi'

async function fetchAccount(accountID: string): Promise<Account> {
  const res = await apiService.accounts.getById(accountID)
  if (!res.success) throw new Error(res.error ?? 'Failed to fetch account')
  return res.data!
}

// Account names and logos are static within a session — fetch once, never auto-refetch
const ACCOUNT_QUERY_OPTIONS = {
  staleTime: Infinity,
  gcTime: Infinity,
} as const

export function useInfiniteListAccounts() {
  return useInfiniteQuery({
    queryKey: ['accounts', 'list'],
    queryFn: async ({ pageParam }) => {
      const res = await apiService.accounts.getAll(pageParam as number)
      if (!res.success) throw new Error(res.error ?? 'Failed to list accounts')
      return res.data as AccountsPage
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.accounts.length, 0)
      return loaded < lastPage.totalCount ? allPages.length + 1 : undefined
    },
    ...ACCOUNT_QUERY_OPTIONS,
  })
}

export function useAccount(accountID: string) {
  return useQuery({
    queryKey: ['accounts', accountID],
    queryFn: () => fetchAccount(accountID),
    enabled: !!accountID,
    ...ACCOUNT_QUERY_OPTIONS,
  })
}

export function useAccounts(accountIDs: string[]): Record<string, Account | undefined> {
  const results = useQueries({
    queries: accountIDs.map((id) => ({
      queryKey: ['accounts', id] as const,
      queryFn: () => fetchAccount(id),
      ...ACCOUNT_QUERY_OPTIONS,
    })),
  })

  return Object.fromEntries(
    accountIDs.map((id, i) => [id, results[i].data])
  )
}

/**
 * Returns a stable map of accountID → account name for all accounts
 * referenced by the loaded pools list.
 */
export function useAccountNames(): Record<string, string> {
  const { data: pools = [] } = usePools()

  const uniqueIds = useMemo(
    () => [...new Set(pools.map((p) => p.accountID))],
    [pools]
  )

  const accounts = useAccounts(uniqueIds)

  return useMemo(() => {
    const map: Record<string, string> = {}
    for (const [id, account] of Object.entries(accounts)) {
      if (account?.name) map[id] = account.name
    }
    return map
  }, [accounts])
}
